import { useActivityDrawer } from '../shell/Dock';
import { ActLink } from './ActLink';
import type { Activity } from '@dpw/shared';
import { TYPE_LABELS } from '@dpw/shared';
import { TypeIcon } from './icons';
import { Diamond, Owners, Progress, StatusPill } from './bits';
import { dayLabel, localDay, localTime, agoStart } from '../lib/format';
import { useNewSince } from '../lib/seen';
import { NewDot } from './NewDot';

export function whenText(a: Activity) {
  if (a.startsAt) return `${dayLabel(localDay(a.startsAt))} ${localTime(a.startsAt)}`;
  if (a.dueDate) return `Due ${dayLabel(a.dueDate)}${a.dueTime ? ` ${a.dueTime}` : ''}`;
  return 'No date';
}

function prep(a: Activity) {
  if (a.status === 'done') return { pct: 100, label: 'Done', color: 'var(--grn)' };
  if (a.stepsTotal) return { pct: a.progress, label: `${a.stepsDone} of ${a.stepsTotal}`, color: a.progress >= 60 ? 'var(--grn)' : 'var(--amb)' };
  if (a.waitingOn) return { pct: 0, label: 'Waiting', color: 'var(--line2)' };
  if (a.progress) return { pct: a.progress, label: `${a.progress}%`, color: a.progress >= 60 ? 'var(--grn)' : 'var(--amb)' };
  return { pct: 0, label: 'Not started', color: 'var(--line2)' };
}

export function ActivityTable({ rows, showTrack = true }: { rows: Activity[]; showTrack?: boolean }) {
  // A row opens its activity in the activity panel, over this list (6.2).
  const drawer = useActivityDrawer();
  // The blue "new" dot on the kind icon: someone else added it, or added to it, since you opened it (6.3).
  const dots = useNewSince();
  if (!rows.length) return <div className="empty">Nothing here with these filters.</div>;
  return (
    <div className="atbox">
      <table className="at">
        <thead><tr><th className="c-icon" style={{ width: 38 }} /><th className="c-act">Activity</th>{showTrack && <th className="c-trk">Track</th>}<th className="c-meta">Owners</th><th className="c-meta">When</th><th className="c-prog">Progress</th><th className="c-stat">Status</th><th className="c-upd">Updated</th></tr></thead>
        <tbody>
          {rows.map(a => {
            const p = prep(a);
            return (
              <tr className="arow" key={a.id} data-new={dots.row(a.id) || undefined} onClick={e => { if (!(e.target as Element).closest('a, button, input, select, label')) drawer.open(a.id); }}>
                <td className="c-icon"><span className="ti"><TypeIcon type={a.type} />{dots.row(a.id) && <NewDot />}</span></td>
                <td className="c-act">
                  <ActLink className="ttl" id={a.id}>{a.title}</ActLink>
                  <div className="xs mut rowc" style={{ gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                    <span>{TYPE_LABELS[a.type]}</span>
                    {a.isMilestone && <><span>·</span><Diamond sm /><span>Milestone</span></>}
                    {a.isClientRequest && <span className="lbl client">Client request</span>}
                    {a.waitingOn && <span className="lbl wait">Waiting on {a.waitingOn.name}</span>}
                    {a.tags.length > 0 && <span className="mono">{a.tags.map(t => `#${t}`).join(' ')}</span>}
                  </div>
                </td>
                {showTrack && <td className="c-trk"><span className="tchip"><i style={{ background: a.trackColor }} />{a.trackName}</span></td>}
                <td className="c-meta"><Owners owners={a.owners} /></td>
                <td className="c-meta mono xs" style={{ whiteSpace: 'nowrap', color: a.isOverdue ? 'var(--red-tx)' : undefined }}>{whenText(a)}</td>
                <td className="c-prog"><div className="pp"><Progress value={p.pct} color={p.color} /><span className="xs mut" style={{ width: 70 }}>{p.label}</span></div></td>
                <td className="c-stat"><StatusPill status={a.status} overdue={a.isOverdue} /></td>
                <td className="c-upd xs mut" style={{ whiteSpace: 'nowrap' }}>{agoStart(a.updatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
